import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { brand, navLinks, contactInfo } from '../mock/mock';

/* ---------- Scroll reveal wrapper ---------- */
export function Reveal({ children, delay = 0, className = '', as: Tag = 'div' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ---------- Section label (red bar + antetítulo) ---------- */
export function SectionLabel({ children, light = false }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className="mt-red-bar" />
      <span className={`font-display uppercase tracking-[0.28em] text-xs font-medium ${light ? 'text-white/70' : 'text-[var(--mt-muted)]'}`}>
        {children}
      </span>
    </div>
  );
}

/* ---------- Buttons ---------- */
export function BtnRed({ to, href, children, className = '', onClick, type }) {
  const cls = `group inline-flex items-center justify-center gap-2 bg-[var(--mt-red)] hover:bg-[var(--mt-red-dark)] text-white font-display uppercase tracking-wider text-sm font-semibold px-8 py-4 transition-colors duration-300 ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (type === 'submit') return <button type="submit" onClick={onClick} className={cls}>{inner}</button>;
  if (href) return <a href={href} className={cls}>{inner}</a>;
  return <Link to={to} onClick={onClick} className={cls}>{inner}</Link>;
}

export function BtnGhost({ to, children, className = '' }) {
  return (
    <Link
      to={to}
      className={`inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white font-display uppercase tracking-wider text-sm font-medium px-8 py-4 transition-colors duration-300 hover:bg-white/5 ${className}`}
    >
      {children}
    </Link>
  );
}

/* ---------- Navbar ---------- */
export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0b0b0d]/95 backdrop-blur-md border-b border-[var(--mt-border)]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 flex items-center justify-between h-[76px]">
        <Link to="/" className="flex items-center">
          <span className="bg-white rounded-md px-2.5 py-1.5 inline-flex">
            <img src={brand.logo} alt="MusicTeam" className="h-8 w-auto" />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `mt-link-underline font-display uppercase tracking-wider text-[13px] font-medium transition-colors ${
                  isActive ? 'text-white active' : 'text-white/70 hover:text-white'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/hablemos"
            className="bg-[var(--mt-red)] hover:bg-[var(--mt-red-dark)] text-white font-display uppercase tracking-wider text-[13px] font-semibold px-5 py-2.5 transition-colors"
          >
            Activa tu espacio
          </Link>
        </nav>

        <button className="lg:hidden text-white" onClick={() => setOpen((o) => !o)} aria-label="Menú">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-[#0b0b0d] border-b border-[var(--mt-border)] ${
          open ? 'max-h-[420px]' : 'max-h-0'
        }`}
      >
        <nav className="flex flex-col px-6 py-4">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `py-3 border-b border-white/5 font-display uppercase tracking-wider text-sm ${
                  isActive ? 'text-[var(--mt-red)]' : 'text-white/80'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/hablemos"
            className="mt-4 text-center bg-[var(--mt-red)] text-white font-display uppercase tracking-wider text-sm font-semibold px-5 py-3"
          >
            Activa tu espacio
          </Link>
        </nav>
      </div>
    </header>
  );
}

/* ---------- Final CTA banner (reusable) ---------- */
export function CtaBanner({ label, title, redTitle, text, cta = 'Hablemos de tu espacio', to = '/hablemos', image }) {
  return (
    <section className="relative overflow-hidden">
      {image && (
        <>
          <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0b0b0d]/85" />
        </>
      )}
      <div className="relative max-w-[1000px] mx-auto px-5 md:px-8 py-24 md:py-32 text-center">
        {label && (
          <Reveal>
            <p className="font-display uppercase tracking-[0.28em] text-xs text-white/60 mb-6">{label}</p>
          </Reveal>
        )}
        <Reveal delay={80}>
          <h2 className="font-display font-bold uppercase text-4xl md:text-6xl leading-[1.02] text-white">
            {title}
            {redTitle && <span className="block text-[var(--mt-red)]">{redTitle}</span>}
          </h2>
        </Reveal>
        {text && (
          <Reveal delay={160}>
            <p className="mt-6 text-white/70 max-w-xl mx-auto leading-relaxed">{text}</p>
          </Reveal>
        )}
        <Reveal delay={240}>
          <div className="mt-9">
            <BtnRed to={to}>{cta}</BtnRed>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
export function Footer() {
  return (
    <footer className="bg-[#08080a] border-t-2 border-[var(--mt-red)]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <span className="bg-white rounded-md px-3 py-2 inline-flex mb-5">
              <img src={brand.logo} alt="MusicTeam" className="h-9 w-auto" />
            </span>
            <p className="text-white/60 max-w-sm leading-relaxed">{brand.tagline}</p>
            <p className="text-white/40 mt-4 max-w-sm text-sm leading-relaxed">
              {brand.slogan} Desde la primera idea hasta el momento en que comienza la experiencia.
            </p>
          </div>

          <div>
            <h4 className="font-display uppercase tracking-wider text-sm text-white mb-5">Navegación</h4>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/55 hover:text-[var(--mt-red)] transition-colors text-sm">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/hablemos" className="text-white/55 hover:text-[var(--mt-red)] transition-colors text-sm">
                  Hablemos
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display uppercase tracking-wider text-sm text-white mb-5">Contacto</h4>
            <ul className="space-y-3 text-sm">
              <li className="text-white/55">{contactInfo.email}</li>
              <li className="text-white/55">{contactInfo.phone}</li>
              <li className="text-white/55">{contactInfo.location}</li>
            </ul>
            <div className="flex gap-4 mt-5">
              {contactInfo.socials.map((s) => (
                <a key={s.label} href={s.url} className="text-white/45 hover:text-[var(--mt-red)] transition-colors text-xs uppercase tracking-wider font-display">
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/35 text-xs">© 2024 MusicTeam Management. Todos los derechos reservados.</p>
          <p className="text-white/35 text-xs font-display uppercase tracking-widest">Música y entretenimiento con sentido</p>
        </div>
      </div>
    </footer>
  );
}
